import "./prism-retrieval.css"

import { useTranslation } from "react-i18next"

import type { PrismSource } from "./prism-retrieval-constants"
import type { FilthSearchActivityState } from "../../utils/filth-search-activity"

import {
  PrismNamedBeforeContext,
  PrismTurnKeyContext,
  usePrismTurn,
} from "@/features/chat/hooks/use-prism-turn"
import { usePrismView } from "@/features/chat/hooks/use-prism-view"
import { cn } from "@/lib/utils"

import { PrismAccessPanel } from "./prism-access-panel"
import { PrismDepthPanel } from "./prism-depth-panel"
import { PrismFlowRow } from "./prism-flow-row"
import { PrismFocusPanel } from "./prism-focus-panel"
import { PrismLevels } from "./prism-levels"

type PrismRetrievalProps = {
  /** Parsed `details.filth_search` of this turn. */
  state: FilthSearchActivityState
  /** This turn's own cited FILTH sources; never search results. */
  sources: PrismSource[]
  /** The answer is still being written. Only a live turn animates. */
  isLive: boolean
  /**
   * The answer is written but the documents that tell its FILTH citations
   * apart have not arrived yet (they come with the saved message). The turn
   * stays live meanwhile, so the sources are named when they are known.
   */
  awaitingSources?: boolean
  /**
   * Identifies this turn across a remount of its message (session and
   * position), so a live turn carries on instead of starting over.
   */
  turnKey?: string
  /**
   * The answer's own citation click handler, called with the 0-based index a
   * click on the same superscript passes. Without it the chips are plain text.
   */
  onSourceClick?: (citationIndex: number) => void
  /** How many other distinct sources the answer cites. Never named here. */
  otherCitedCount?: number
  /**
   * The answer's citations can already be told apart (its documents, or the
   * FILTH citation keys of its grounded search, have arrived), so the other
   * sources may be counted while the answer is still written.
   */
  otherSourcesKnown?: boolean
  /** Brings the answer's citation list into view. Without it the line is text. */
  onOtherSourcesClick?: () => void
}

const OTHER_SOURCES_CLASS = "text-muted-foreground text-xs"

/**
 * Prism (#3261): how secure retrieval is shown above an answer, in the view
 * the user chose, else the company default, else Levels (`usePrismView`).
 * It names only the answer's FILTH citations, under the answer's own numbers,
 * and opens them through the answer's own citation handler. Any other cited
 * source stays in the answer's citation list; Prism only counts it.
 */
export function PrismRetrieval({
  state,
  sources: citedSources,
  isLive: answering,
  awaitingSources = false,
  turnKey,
  onSourceClick,
  otherCitedCount = 0,
  otherSourcesKnown = false,
  onOtherSourcesClick,
}: PrismRetrievalProps) {
  const { t } = useTranslation()
  const view = usePrismView()
  // The views follow the turn, not the stream: they name each cited source
  // once it is identified, one beat at a time, and settle only after that.
  const turn = usePrismTurn({
    turnKey,
    answering,
    awaitingSources,
    sourceCount: citedSources.length,
  })
  const isLive = turn.live
  const sources =
    turn.named < citedSources.length ? citedSources.slice(0, turn.named) : citedSources

  let content
  switch (view) {
    case "focus":
      content = (
        <PrismFocusPanel
          isLive={isLive}
          sources={sources}
          state={state}
          onSourceClick={onSourceClick}
        />
      )
      break
    case "access":
      content = (
        <PrismAccessPanel
          isLive={isLive}
          sources={sources}
          state={state}
          onSourceClick={onSourceClick}
        />
      )
      break
    case "depth":
      content = (
        <PrismDepthPanel
          isLive={isLive}
          sources={sources}
          state={state}
          onSourceClick={onSourceClick}
        />
      )
      break
    case "flow":
      content = <PrismFlowRow isLive={isLive} state={state} />
      break
    default:
      content = (
        <PrismLevels
          isLive={isLive}
          sources={sources}
          state={state}
          onSourceClick={onSourceClick}
        />
      )
  }

  // Counted as soon as the citations can be told apart: while the answer is
  // written when the grounded search reported its FILTH citation keys, else
  // once the turn has settled. Before that a FILTH citation cannot be told
  // from any other, and a count that changed would move the answer.
  const showOtherSources = otherCitedCount > 0 && (!isLive || otherSourcesKnown)
  let otherSources = null
  if (showOtherSources) {
    const label = t("activity_messages.filth.otherCitedSources", {
      count: otherCitedCount,
      defaultValue: "The answer also cites {{count}} other sources",
    })
    // The answer's citation list, which the line scrolls to, is mounted only
    // once the turn is over; until then the line is text.
    otherSources =
      onOtherSourcesClick && !isLive ? (
        <button
          className={`${OTHER_SOURCES_CLASS} hover:text-foreground focus-visible:outline-foreground cursor-pointer self-start rounded-sm text-left underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2`}
          data-testid="prism-other-sources"
          type="button"
          onClick={onOtherSourcesClick}
        >
          {label}
        </button>
      ) : (
        <p className={OTHER_SOURCES_CLASS} data-testid="prism-other-sources">
          {label}
        </p>
      )
  }

  return (
    <PrismTurnKeyContext.Provider value={turnKey}>
      <PrismNamedBeforeContext.Provider value={turn.namedBefore}>
        <section
          aria-label={t("activity_messages.filth.title", { defaultValue: "Prism" })}
          className="flex flex-col"
          data-prism-live={isLive}
          data-prism-view={view}
          data-testid="prism-retrieval"
        >
          {content}
          {/* Opens on Focus while the answer is written, so the line never
              pushes the answer down in one frame. Its spacing is inside, so a
              closed line takes no room at all. */}
          <div
            className={cn("prism-reveal", turn.motion && "prism-motion")}
            data-open={showOtherSources}
            data-testid="prism-other-sources-reveal"
          >
            <div>{otherSources && <div className="flex flex-col pt-1.5">{otherSources}</div>}</div>
          </div>
        </section>
      </PrismNamedBeforeContext.Provider>
    </PrismTurnKeyContext.Provider>
  )
}
